import {
  ActionIcon,
  Badge,
  Chip,
  Group,
  Menu,
  Popover,
  Stack,
  Tooltip,
} from "@mantine/core";
import React, { useCallback, useEffect, useState } from "react";
import { useProfileStore } from "../../../../stores/UserStore";
import { useLanguage } from "../../../../stores/LanguageStore";
import { IconChevronDown } from "@tabler/icons-react";
import {
  filtersAreEqual,
  transformFilterToServer,
} from "../../../../functions/utilFunctions";

function getDefaultPinned() {
  const lang = useLanguage.getState().language;
  return [
    {
      name: lang.components.default_filters.tpe,
      tooltip: lang.components.default_filters.tpe_tip,
      filter: {
        mode: "or",
        values: [
          { field_name: "13", type: "equals", value: "0-10", id: 0 },
          { field_name: "13", type: "equals", value: "11-50", id: 1 },
        ],
      },
      pinned: false,
      pinnedToToolbar: false,
      id: 0,
    },
    {
      name: lang.components.default_filters.pme,
      tooltip: lang.components.default_filters.pme_tip,
      filter: {
        mode: "or",
        values: [
          { field_name: "13", type: "equals", value: "51-200", id: 0 },
          { field_name: "13", type: "equals", value: "201-500", id: 1 },
          { field_name: "13", type: "equals", value: "501-1000", id: 2 },
        ],
      },
      pinned: false,
      pinnedToToolbar: false,
      id: 1,
    },
    {
      name: lang.components.default_filters.great_company,
      tooltip: lang.components.default_filters.great_company_tip,
      filter: {
        mode: "or",
        values: [
          { field_name: "13", type: "equals", value: "1001-5000", id: 0 },
          { field_name: "13", type: "equals", value: "5001-10000", id: 1 },
          { field_name: "13", type: "equals", value: "10001+", id: 2 },
        ],
      },
      pinned: false,
      pinnedToToolbar: false,
      id: 2,
    },
  ];
}

const ContactsExteriorFilters = ({
  apiRef,
  loading,
  screenIsLarge,
  filterKey = "contact_lists_contacts",
  limit=3
}) => {
  const lang = useLanguage((s) => s.language);
  const profile = useProfileStore((s) => s.profile);
  const [pinned, setPinned] = useState([]);
  const [actualFilterName, setActualFilterName] = useState(null);

  const applyFilter = useCallback(
    (filterItem) => {
      const newFilter =
        actualFilterName === filterItem?.name ? null : filterItem.filter;
      const quickFilter =
        apiRef.current.state.filter.filterModel.quickFilterValues?.join(" ");

      function getItems(v, i) {
        if (v?.values && v?.mode) {
          return {
            mode: v?.mode,
            values: v.values.map((_v, _i) => getItems(_v, _i)),
            id: v?.id || i,
          };
        }
        return {
          field: v.field_name,
          value: v.value,
          operator: v.type,
          id: v?.id || i,
        };
      }

      let newTableFilter = {
        items: newFilter?.values?.map((val) => getItems(val)) || [],
        logicOperator: newFilter?.mode || "and",
        quickFilterValues: quickFilter?.split(" ") || [],
      };

      apiRef.current.setFilterModel(newTableFilter);
      setActualFilterName(
        actualFilterName === filterItem?.name ? null : filterItem.name,
      );
    },
    [apiRef, actualFilterName],
  );

  useEffect(() => {
    let allFilters =
      profile?.saved_filters?.[filterKey]?.map((item, index) => ({
        ...item,
        id: index + 2,
      })) || [];

    let userPinned = allFilters?.filter((x) => x.pinnedToToolbar) || [];

    setPinned([...userPinned, ...getDefaultPinned()]);
  }, [profile?.saved_filters, filterKey]);

  useEffect(() => {
    if (loading) {
      let actualFilterModel = apiRef.current.state.filter.filterModel;
      let actualFilter = transformFilterToServer(actualFilterModel);

      let sameFilter = pinned.find((item) =>
        filtersAreEqual(actualFilter, item.filter),
      );

      if (sameFilter) {
        setActualFilterName(sameFilter?.name);
      } else {
        setActualFilterName(null);
      }
    }
  }, [loading, apiRef, pinned]);

  return (
    <Group mr={"auto"} wrap="nowrap">
      {pinned.slice(0, limit).map((item) => (
        <Tooltip
          key={item.id}
          label={item?.tooltip}
          disabled={!item?.tooltip}
          refProp="rootRef"
        >
          <Chip
            key={item.id}
            color="primary"
            styles={{
              label: {
                background:
                  actualFilterName === item?.name
                    ? "var(--mantine-color-primary-6)"
                    : "var(--mantine-color-primary-0)",
              },
            }}
            onClick={() => applyFilter(item)}
            checked={actualFilterName === item?.name}
            size={screenIsLarge ? "md" : "xs"}
            mt={-4}
          >
            {item.name}
          </Chip>
        </Tooltip>
      ))}
      {pinned.length > limit && (
        <Popover>
          <Popover.Target>
            <ActionIcon variant="subtle">
              <IconChevronDown />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <Group>
              {pinned.slice(limit).map((item) => (
                <Tooltip
                  key={item.id}
                  label={item?.tooltip}
                  disabled={!item?.tooltip}
                  refProp="rootRef"
                >
                  <Chip
                    key={item.id}
                    color="primary"
                    onClick={() => applyFilter(item)}
                    checked={actualFilterName === item?.name}
                    size="sm"
                    styles={{
                      label: {
                        background:
                          actualFilterName === item?.name
                            ? "var(--mantine-color-primary-6)"
                            : "var(--mantine-color-primary-0)",
                      },
                    }}
                  >
                    {item.name}
                  </Chip>
                </Tooltip>
              ))}
            </Group>
          </Popover.Dropdown>
        </Popover>
      )}
    </Group>
  );
};

export default ContactsExteriorFilters;
